import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiData, ApiMessage, ApiResponse } from '../../interfaces/api/api-response.interface';
import {
  AgentPlayerPermissionFlags,
  AgentPlayerPermissions,
  User,
  UserBlockHistory,
  UserNgSelect,
  UserNgSelectData,
} from '../../interfaces/api/user.interface';
import { NgSelect } from '../../interfaces/ui/ui.interface';
import {
  SKIP_AUTH_HEADER,
  SKIP_ERROR_NOTIFICATION_HEADER,
} from '../../helpers/global/global.constant';
import { ToastrNotificationService } from '../ui/toastr-notification.service';
import { DEFAULT_SEARCH_NG_SELECT_PAGINATION } from '../../helpers/ui/ui.constant';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  public API_URL = `${environment.apiUrl}/user`;

  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService
  ) {}

  public getMeUser(): Observable<ApiResponse<User>> {
    return this._httpClient.get<ApiResponse<User>>(`${this.API_URL}/get-me-user`);
  }

  public getAllUsersNgSelect(
    filter: UserNgSelect = {},
    page: number = DEFAULT_SEARCH_NG_SELECT_PAGINATION.PAGE,
    limit: number = DEFAULT_SEARCH_NG_SELECT_PAGINATION.LIMIT
  ): Observable<ApiResponse<UserNgSelectData>> {
    const endpoint = `${this.API_URL}/get-all-user-by-type`;
    const params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    return this._httpClient.post<ApiResponse<UserNgSelectData>>(endpoint, filter, { params });
  }

  public findUsers(filter: object): Observable<ApiResponse<ApiData<User[]>>> {
    const endpoint = `${this.API_URL}/find`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<User[]>>>(endpoint, {
      params,
    });
  }

  public getAllUserBySelect(filter: object): Observable<ApiResponse<ApiData<User[]>>> {
    const endpoint = `${this.API_URL}/get-all-user-by-select`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<User[]>>>(endpoint, {
      params,
    });
  }

  public findUsersForSelect(filter: object): Observable<ApiData<NgSelect<string>[]>> {
    return this.getAllUserBySelect(filter).pipe(
      map((response) => ({
        totalCount: response.data.totalCount,
        result: response.data.result.map((user) => ({
          label: user.username,
          value: user._id,
          id: user.userId,
          labelCombined: `${user.userId} - ${user.username}`,
          currencies: user.currencies,
          currencyCode: user.currencyId,
        })),
      }))
    );
  }

  public findUserAdminForSelect(
    filter: UserNgSelect = {},
    page: number = DEFAULT_SEARCH_NG_SELECT_PAGINATION.PAGE,
    limit: number = DEFAULT_SEARCH_NG_SELECT_PAGINATION.LIMIT
  ): Observable<ApiResponse<UserNgSelectData>> {
    const endpoint = `${this.API_URL}/get-super-admin-users`;
    let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    if (filter.username) {
      params = params.set('username', filter.username);
    }

    if (filter.roleName) {
      params = params.set('roleName', filter.roleName);
    }

    return this._httpClient.get<ApiResponse<UserNgSelectData>>(endpoint, {
      params,
    });
  }
  public createUser(user: object): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/register`;
    return this._httpClient
      .post<ApiResponse<User>>(endpoint, user)
      .pipe(tap((res) => this.showNotification(res.message, 'userManagement.users.title')));
  }

  public updateProfile(user: User): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/update-profile`;
    return this._httpClient
      .patch<ApiResponse<User>>(endpoint, user)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public setUserPassword(id: string, newPassword: string): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/set-user-password`;
    return this._httpClient
      .put<ApiResponse<User>>(endpoint, { id, newPassword })
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public generateTwoFactor(id: string): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/generate-two-factor`;
    return this._httpClient
      .post<ApiResponse<User>>(endpoint, { id })
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public activateTwoFactor(id: string, code: string): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/enable-two-factor`;
    return this._httpClient
      .put<ApiResponse<User>>(endpoint, { id, code })
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public disableTwoFactor(id: string): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/disable-two-factor`;
    return this._httpClient
      .put<ApiResponse<User>>(endpoint, { id })
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public verifyLogin(twoFactorToken: string, code: string): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/verify-login`;
    const headers = new HttpHeaders({
      [SKIP_AUTH_HEADER]: SKIP_AUTH_HEADER,
      [SKIP_ERROR_NOTIFICATION_HEADER]: 'true',
    });
    return this._httpClient
      .post<ApiResponse<User>>(endpoint, { twoFactorToken, code }, { headers })
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public updateUser(id: string, user: object): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/update-user/${id}`;
    return this._httpClient
      .put<ApiResponse<User>>(endpoint, user)
      .pipe(tap((res) => this.showNotification(res.message, 'userManagement.users.title')));
  }

  public updateAgentCommissionHold(body: {
    userId: string;
    balance: number;
    currencyId: string;
  }): Observable<ApiResponse<User>> {
    return this._httpClient.put<ApiResponse<User>>(
      `${this.API_URL}/update-agent-commission-hold`,
      body
    );
  }

  public getMeCashdesks(): Observable<ApiResponse<User>> {
    return this._httpClient.get<ApiResponse<User>>(`${this.API_URL}/get-me-cashdesks`);
  }

  public setUserLockStatus(body: {
    userId: string;
    isLocked: boolean;
    reason: string;
  }): Observable<ApiResponse<User>> {
    const endpoint = `${this.API_URL}/set-lock-status`;
    return this._httpClient
      .put<ApiResponse<User>>(endpoint, body)
      .pipe(tap((res) => this.showNotification(res.message, 'userManagement.users.title')));
  }

  public getUserBlockHistory(
    userId: string,
    filter: object = {}
  ): Observable<ApiResponse<ApiData<UserBlockHistory[]>>> {
    const endpoint = `${this.API_URL}/block-history/${userId}`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<UserBlockHistory[]>>>(endpoint, { params });
  }

  public getAgentPlayerPermissions(
    agentId: string
  ): Observable<ApiResponse<AgentPlayerPermissions>> {
    const endpoint = `${this.API_URL}/player-permissions/${agentId}`;
    return this._httpClient.get<ApiResponse<AgentPlayerPermissions>>(endpoint);
  }

  public updateAgentPlayerPermissions(
    agentId: string,
    permissions: AgentPlayerPermissionFlags
  ): Observable<ApiResponse<AgentPlayerPermissions>> {
    const endpoint = `${this.API_URL}/player-permissions/${agentId}`;
    return this._httpClient
      .put<ApiResponse<AgentPlayerPermissions>>(endpoint, permissions)
      .pipe(tap((res) => this.showNotification(res.message, 'userManagement.users.title')));
  }

  private showNotification(message: ApiMessage, title: string = 'profile.title'): void {
    this._notificationService.showNotification({
      title,
      message,
      type: 'success',
    });
  }
}
